<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Bell, CheckCheck, Clapperboard, Download, Sparkles, AlertTriangle, Webhook } from 'lucide-vue-next'
import { notificationsApi, type AppNotification } from '@/lib/notificationsApi'

const POLL_MS = 60_000

const { t, locale } = useI18n()
const router = useRouter()

const open = ref(false)
const loading = ref(false)
const items = ref<AppNotification[]>([])
const unreadCount = ref(0)
const rootRef = ref<HTMLElement | null>(null)
let timer: number | null = null

const badge = computed(() => (unreadCount.value > 9 ? '9+' : String(unreadCount.value)))

async function refresh() {
  try {
    const data = await notificationsApi.list({ limit: 15 })
    items.value = data.notifications
    unreadCount.value = data.unread_count
  } catch {
    /* header polling must never surface errors */
  }
}

function iconFor(type: string) {
  if (type === 'tour-deletion-scheduled') return AlertTriangle
  if (type === 'webhook-disabled') return Webhook
  if (type.startsWith('video')) return Clapperboard
  if (type.startsWith('import')) return Download
  if (type.endsWith('failed')) return AlertTriangle
  return Sparkles
}

function isFailure(type: string): boolean {
  return type.endsWith('failed') || type === 'webhook-disabled'
}

function labelFor(n: AppNotification): string {
  const key = `notifications.types.${n.type}`
  const name = n.title || t('notifications.untitled')
  return t(key, { name })
}

function timeAgo(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale.value, { numeric: 'auto' })
  if (seconds < 60) return t('notifications.justNow')
  if (seconds < 3600) return rtf.format(-Math.round(seconds / 60), 'minute')
  if (seconds < 86400) return rtf.format(-Math.round(seconds / 3600), 'hour')
  return rtf.format(-Math.round(seconds / 86400), 'day')
}

async function openItem(n: AppNotification) {
  open.value = false
  if (!n.read) {
    n.read = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    notificationsApi.markRead([n.uuid]).catch(() => undefined)
  }
  if (n.link) await router.push(n.link)
}

async function markAll() {
  items.value = items.value.map(n => ({ ...n, read: true }))
  unreadCount.value = 0
  notificationsApi.markAllRead().catch(() => undefined)
}

function toggle() {
  open.value = !open.value
  if (open.value) void refresh()
}

function onClickOutside(e: MouseEvent) {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target as Node)) open.value = false
}

function onVisibility() {
  if (!document.hidden) void refresh()
}

onMounted(() => {
  void refresh()
  timer = window.setInterval(() => { if (!document.hidden) void refresh() }, POLL_MS)
  document.addEventListener('click', onClickOutside)
  document.addEventListener('visibilitychange', onVisibility)
})
onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer)
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('visibilitychange', onVisibility)
})
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="relative flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground transition-colors hover:bg-muted"
      :title="t('notifications.title')"
      :aria-label="t('notifications.title')"
      @click.stop="toggle"
    >
      <Bell class="h-4 w-4" />
      <span
        v-if="unreadCount > 0"
        class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white"
      >{{ badge }}</span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border bg-background shadow-xl"
    >
      <div class="flex items-center justify-between border-b bg-muted/40 px-4 py-2.5">
        <span class="text-sm font-semibold">{{ t('notifications.title') }}</span>
        <button
          v-if="unreadCount > 0"
          type="button"
          class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          @click="markAll"
        >
          <CheckCheck class="h-3.5 w-3.5" /> {{ t('notifications.markAllRead') }}
        </button>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <button
          v-for="n in items"
          :key="n.uuid"
          type="button"
          class="flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
          :class="n.read ? 'opacity-70' : ''"
          @click="openItem(n)"
        >
          <span
            class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            :class="isFailure(n.type) ? 'bg-red-100 text-red-600 dark:bg-red-950' : 'bg-primary/10 text-primary'"
          >
            <component :is="iconFor(n.type)" class="h-3.5 w-3.5" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block text-sm leading-snug">{{ labelFor(n) }}</span>
            <span v-if="n.body" class="mt-0.5 block truncate text-xs text-muted-foreground">{{ n.body }}</span>
            <span class="mt-0.5 block text-[11px] text-muted-foreground">{{ timeAgo(n.created_at) }}</span>
          </span>
          <span v-if="!n.read" class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
        </button>

        <div v-if="!items.length && !loading" class="px-4 py-8 text-center text-sm text-muted-foreground">
          {{ t('notifications.empty') }}
        </div>
      </div>
    </div>
  </div>
</template>
